import * as React from 'react'
import { userTypeToIconName } from '../../../../common/src/components/Icon'
import { FetchUserDetailsQuery, UserType } from '../../../../common/src/types.gen'
import validate from '../../../../common/src/validate'
import { Field } from '../components/EditBox'
import EditBoxes from '../components/EditBoxes'

interface UserEditBoxesProps {
  user: FetchUserDetailsQuery['user']
  onPatch: (patch: any) => any
}

export default class UserEditBoxes extends React.PureComponent<UserEditBoxesProps> {
  public render() {
    const { user } = this.props

    const fields = [
      this.createEmailField(),
      this.createUserTypeField(),
      this.createSlackUserIdField(),
      this.createGithubUsernameField(),
      this.createTeamField(),
    ]

    if (user.userType === UserType.ENGINEER) {
      fields.push(this.createPagerDutyIdField())
    }

    return <EditBoxes model={user} fields={fields} displayType="small" onEdit={this.onEditBound} />
  }

  private createEmailField(): Field<string> {
    return {
      key: 'email',
      isEditable: true,
      textFunc: (val?: string) => val || 'No email',
      editTextFunc: (val?: string) => val || '',
      validator: validate.email().empty(''),
      placeholder: 'lisa@appfolio.com',
      iconFunc: () => 'channel-email',
    }
  }

  private createUserTypeField(): Field<UserType> {
    return {
      key: 'userType',
      isEditable: true,
      options: Object.keys(UserType) as UserType[],
      iconFunc: (val: UserType) => userTypeToIconName(val),
      tooltip:
        'Each user type has different access to Command Center. In increasing order of power:\n\n' +
        'SALES — can view inbox but change nothing. Typical for sales reps.\n\n' +
        'OPERATOR — responds to prospects as Lisa.\n\n' +
        'SENIOR_OPERATOR — responds to both prospects and boss/agents as Lisa.\n\n' +
        'TRAINING — operator in training. Typically for lowering max thread count.\n\n' +
        'CUSTOMER_STEWARD - responds to boss and agent threads only.\n\n' +
        'CUSTOMER_STEWARD_TRAINING - same as CUSTOMER_STEWARD but with lower thread count.\n\n' +
        'TEAM_LEAD — handles escalations from the Senior Operators\n\n\n' +
        'ANALYST — ops team member doing analysis\n\n' +
        'OPS_MANAGER — handles escalations from Team Leads, change any mutable field, change other users\n\n\n' +
        'ENGINEER/SUPPORT/ANALYST — can change any mutable field, change other users',
    }
  }

  private createSlackUserIdField(): Field<string> {
    return {
      key: 'slackUserId',
      isEditable: true,
      iconFunc: () => 'logo-slack',
      textFunc: (val?: string) => (val === undefined ? 'no Slack' : val),
      editTextFunc: (val?: string) => val || '',
    }
  }

  private createGithubUsernameField(): Field<string> {
    return {
      key: 'githubUsername',
      isEditable: true,
      iconFunc: () => 'logo-github',
      textFunc: (val?: string) => (val === undefined ? 'no GitHub' : 'GitHub: ' + val),
      editTextFunc: (val?: string) => val || '',
    }
  }

  private createTeamField(): Field<string> {
    return {
      key: 'teamName',
      isEditable: true,
      textFunc: (val?: any) => (val === undefined ? 'no team' : 'team: ' + val),
      editTextFunc: (val?: any) => val ?? '',
    }
  }

  private createPagerDutyIdField(): Field<string> {
    return {
      key: 'pagerDutyId',
      isEditable: true,
      iconFunc: () => 'logo-pagerduty',
      textFunc: (val?: string) => (val === undefined ? 'no PagerDuty' : val),
      editTextFunc: (val?: string) => val || '',
    }
  }

  private onEditBound = (key: string, value: any) => {
    const { onPatch } = this.props
    const patch: any = {}
    patch[key] = value
    return onPatch(patch)
  }
}
